import axios from 'axios';
import { SYSTEMDOX_API_URL } from '@/lib/apiConfig';

const api = axios.create({ baseURL: SYSTEMDOX_API_URL });

/** Lifecycle document types (specs/changes/lifecycle-doc-model R1). */
export type DocType = 'requirement' | 'design' | 'doc';

/** Body scaffold for a new design document (#544, expanded ADR-022 Rev 2). */
export type DesignTemplate = 'adr' | 'design-doc' | 'rfc' | 'test-plan';

/** Any body scaffold create_spec accepts: BDD/PRD for requirements, ADR/design-doc/RFC/test-plan for design. */
export type SpecTemplate = DesignTemplate | 'bdd' | 'prd';

export interface SpecSummary {
  draftId: string;
  name: string;
  repoFullName: string;
  status: string;
  docType: DocType;
  requirementCount: number;
  createdAt: string;
  updatedAt: string;
  /** Set when the document was published straight to the Knowledge Base
   *  (kb-publish R6) — absent for never-published or propose-only docs. */
  publishedAt?: string;
  /** Git-source pointer (ADR-033): the committed file's repo and path; ''
   *  until the draft is committed. */
  gitRepo?: string;
  gitPath?: string;
}

export interface SpecRequirement {
  reqId: string;
  name: string;
  content: string;
}

export interface SpecDetail {
  draftId: string;
  name: string;
  repoFullName: string;
  status: string;
  docType: DocType;
  proposalContent: string;
  taskContent: string;
  requirements: SpecRequirement[];
  createdAt: string;
  updatedAt: string;
  /** Knowledge Base publish state (kb-publish R6). */
  publishedAt?: string;
  lastPublishRepo?: string;
  publishedPath?: string;
  /** Git-source pointer (ADR-033): once committed, the document's content lives
   *  in git at gitRepo/gitPath and is edited in the reader, not here. Empty
   *  until the draft is committed (by commit-on-generate or publish). */
  gitRepo?: string;
  gitPath?: string;
  syncState?: string;
}

/** 'publish' commits to the repo's default branch and the KB rebuilds
 *  automatically; 'propose' keeps the branch + pull-request flow (kb-publish R1). */
export type PublishMode = 'publish' | 'propose';

export interface PublishResult {
  draftId: string;
  repoFullName: string;
  branch: string;
  filesCommitted: number;
  prUrl: string | null;
  mode?: PublishMode;
  commitSha?: string;
  /** The repo's slug on the published site — the KB link's first segment. */
  publishPath?: string;
}

export interface UnpublishResult {
  draftId: string;
  filesRemoved: number;
}

/** One published-state commit of the document's docs/ folder (kb-publish R6). */
export interface SpecPublishedCommit {
  sha: string;
  fullSha: string;
  message: string;
  date: string;
  author: string;
  htmlUrl: string;
}

/** Version checkpoint metadata (consolidated-full-editor R4) — content stays
 *  behind the detail route. */
export interface SpecVersionSummary {
  versionId: string;
  trigger: 'edit' | 'publish';
  name: string;
  status: string;
  requirementCount: number;
  proposalChars: number;
  taskChars: number;
  contentUpdatedAt: string;
}

export interface SpecVersionDetail {
  versionId: string;
  trigger: string;
  name: string;
  status: string;
  proposalContent: string;
  taskContent: string;
  requirements: SpecRequirement[];
  contentUpdatedAt: string;
}

/** Documentation capability roles (guided-repo-setup): five concerns, each
 *  tri-state. 'unset' = no decision yet (nudges allowed); 'disabled' = a
 *  deliberate opt-out the product stops asking about; 'enabled' names one
 *  dedicated repo — except components, which spans many (each component
 *  documents itself in its own docs/). */
export type CapabilityKey = 'requirements' | 'design' | 'specs' | 'fitness' | 'components';
export type CapabilityStatus = 'unset' | 'enabled' | 'disabled';
export interface Capability {
  status: CapabilityStatus;
  repo?: string;
  repos?: string[];
}
export type Capabilities = Record<CapabilityKey, Capability>;

export const CAPABILITY_KEYS: CapabilityKey[] = [
  'requirements', 'design', 'specs', 'fitness', 'components',
];

/** Dedicated documentation repositories (#571). requirementsRepo/designRepo
 *  are the legacy strings derived server-side from `capabilities` — kept so
 *  pre-capability consumers read on unchanged. Documentation always lives
 *  under docs/ at each repository root. */
export interface DocRepos {
  requirementsRepo: string;
  designRepo: string;
  capabilities?: Capabilities;
}

/** Traceability edge (lifecycle-doc-model R3, #535). For a design doc's
 *  outgoing satisfies edges, targetSpecId/targetName name the requirement;
 *  for a requirement's incoming edges, sourceSpecId/sourceName name the
 *  design doc that satisfies it. */
export interface SpecEdge {
  edgeId: string;
  edgeType: 'satisfies' | 'references';
  sourceSpecId: string;
  targetSpecId?: string;
  targetName?: string;
  sourceName?: string;
  repo?: string;
  path?: string;
  createdAt: string;
  /** Outgoing satisfies edges only (#546): the requirement changed after
   *  this design was last updated, so the design needs review. */
  needsReview?: boolean;
  requirementUpdatedAt?: string;
}

export interface SpecEdges {
  outgoing: SpecEdge[];
  incoming: SpecEdge[];
}

/** A document flagged for review because a requirement it satisfies changed
 *  after it was last updated (lifecycle-doc-model R7, #546). */
export interface StalenessFinding {
  phase: DocType;
  draftId: string;
  name: string;
  updatedAt: string;
  reason: string;
  requirementSpecId: string;
  requirementName: string;
  requirementUpdatedAt: string;
}

export interface LinkStalenessReport {
  findings: StalenessFinding[];
  byPhase: Record<string, StalenessFinding[]>;
  count: number;
}

export const specsApi = {
  setAuthProvider(getToken: () => Promise<string | null>) {
    api.interceptors.request.use(async (config) => {
      const token = await getToken();
      if (token) config.headers.Authorization = `Bearer ${token}`;
      return config;
    });
  },

  async listSpecs(filters?: { status?: string; docType?: DocType; project?: string }): Promise<SpecSummary[]> {
    const params: Record<string, string> = {};
    if (filters?.status) params.status = filters.status;
    if (filters?.docType) params.docType = filters.docType;
    // The selected project scopes the list server-side (GET /specs ?project=);
    // the backend still enforces spaces first.
    if (filters?.project) params.project = filters.project;
    const { data } = await api.get('/specs', {
      params: Object.keys(params).length ? params : undefined,
    });
    return data;
  },

  async getSpec(draftId: string): Promise<SpecDetail> {
    const { data } = await api.get(`/specs/${draftId}`);
    return data;
  },

  async createSpec(
    name: string,
    repoFullName: string,
    author?: string,
    docType: DocType = 'requirement',
    template?: SpecTemplate,
  ): Promise<{ draftId: string }> {
    const { data } = await api.post('/specs', { name, repoFullName, author, docType, template });
    return data;
  },

  /** Create a document with server-side auto-title when `name` is omitted, and
   *  support for a custom `templateId` (capture-templates). The chosen template
   *  (platform `template` or custom `templateId`) is the source of truth for
   *  docType, so the response echoes the resolved type. */
  async createDocument(params: {
    name?: string;
    docType?: DocType;
    template?: SpecTemplate;
    templateId?: string;
    notes?: string;
    repoFullName?: string;
    author?: string;
    /** Stamp the artifact with the selected project (capture stamping). */
    projectId?: string;
  }): Promise<{ draftId: string; docType: DocType; name: string }> {
    const { data } = await api.post('/specs', params);
    return data;
  },

  async updateSpec(draftId: string, updates: Partial<Pick<SpecDetail, 'proposalContent' | 'taskContent' | 'name' | 'status'> & { requirements: SpecRequirement[] }>): Promise<void> {
    await api.put(`/specs/${draftId}`, updates);
  },

  async archiveSpec(draftId: string): Promise<void> {
    await api.post(`/specs/${draftId}/archive`);
  },

  /** Version checkpoints, newest first (consolidated-full-editor R4). */
  async listSpecVersions(draftId: string): Promise<SpecVersionSummary[]> {
    const { data } = await api.get(`/specs/${draftId}/versions`);
    return data.versions;
  },

  /** One checkpoint with full content — the versionId is an ISO timestamp,
   *  so it must travel URL-encoded. */
  async getSpecVersion(draftId: string, versionId: string): Promise<SpecVersionDetail> {
    const { data } = await api.get(`/specs/${draftId}/versions/${encodeURIComponent(versionId)}`);
    return data;
  },

  async publishSpec(draftId: string, options: { repoFullName?: string; targetPath?: string; branch?: string; createPr?: boolean; mode?: PublishMode }): Promise<PublishResult> {
    const { data } = await api.post(`/specs/${draftId}/publish`, options);
    return data;
  },

  /** Commit a freshly generated draft to git as `status: draft` so it is
   *  versioned and searchable from creation (ADR-033, git-source). Graceful:
   *  returns `{committed:false, reason:'no-repo'}` when no repo is available —
   *  the caller ignores that (the draft simply stays in DynamoDB for now). */
  async commitDraft(draftId: string): Promise<{ committed: boolean; reason?: string; repoFullName?: string; path?: string }> {
    const { data } = await api.post(`/specs/${draftId}/commit-draft`, {});
    return data;
  },

  /** Withdraw the document from the Knowledge Base — a removal commit
   *  deleting its docs/ folder (kb-publish R7). */
  async unpublishSpec(draftId: string): Promise<UnpublishResult> {
    const { data } = await api.post(`/specs/${draftId}/unpublish`);
    return data;
  },

  /** The document's published git history — commits touching its docs/
   *  folder (kb-publish R6). Empty when never published. */
  async getPublishedHistory(draftId: string): Promise<SpecPublishedCommit[]> {
    const { data } = await api.get(`/specs/${draftId}/published-history`);
    return data.commits;
  },

  /** Traceability edges touching a document (outgoing + incoming). */
  async listEdges(draftId: string): Promise<SpecEdges> {
    const { data } = await api.get(`/specs/${draftId}/edges`);
    return data;
  },

  /** Link a design document to a requirement it satisfies. */
  async createSatisfiesEdge(designSpecId: string, requirementSpecId: string): Promise<SpecEdge> {
    const { data } = await api.post(`/specs/${designSpecId}/edges`, {
      edgeType: 'satisfies',
      targetSpecId: requirementSpecId,
    });
    return data;
  },

  async deleteEdge(draftId: string, edgeId: string): Promise<void> {
    await api.delete(`/specs/${draftId}/edges/${edgeId}`);
  },

  /** Documents flagged for review because a satisfied requirement changed. */
  async linkStaleness(): Promise<LinkStalenessReport> {
    const { data } = await api.get('/specs/staleness/report');
    return data;
  },

  async getDocRepos(): Promise<DocRepos> {
    const { data } = await api.get('/doc-repos');
    return data;
  },

  async saveDocRepos(repos: DocRepos): Promise<DocRepos> {
    const { data } = await api.put('/doc-repos', repos);
    return data;
  },

  /** Write capability roles (guided-repo-setup shape): provided keys replace,
   *  omitted keys keep their current state server-side. */
  async saveCapabilities(capabilities: Partial<Capabilities>): Promise<DocRepos> {
    const { data } = await api.put('/doc-repos', { capabilities });
    return data;
  },
};
